import { useEffect, type RefObject } from "react";

/**
 * useContainTouch keeps a swipe on a floating sheet from scrolling the page
 * underneath it.
 *
 * `overscroll-behavior: contain` is not enough on its own: it only acts on a
 * box that has something to scroll, so a swipe on an empty chat log, on the
 * sheet's header or on its input went straight through to the book. The body
 * is not locked either — fixing it in place loses the reader's scroll. So a
 * touch move is cancelled unless something inside the sheet can scroll the
 * way the finger is going.
 */
export function useContainTouch(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    let lastX = 0;
    let lastY = 0;
    const onStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      lastX = t.clientX;
      lastY = t.clientY;
    };
    const onMove = (e: TouchEvent) => {
      // Two fingers are a pinch, which zooms: not ours to stop.
      const t = e.touches[0];
      if (e.touches.length !== 1 || !t) return;
      const dx = t.clientX - lastX;
      const dy = t.clientY - lastY;
      lastX = t.clientX;
      lastY = t.clientY;
      if (e.cancelable && !scrollsTowards(e.target, root, dx, dy)) e.preventDefault();
    };
    root.addEventListener("touchstart", onStart, { passive: true });
    // Not passive: a passive listener may not cancel the move.
    root.addEventListener("touchmove", onMove, { passive: false });
    return () => {
      root.removeEventListener("touchstart", onStart);
      root.removeEventListener("touchmove", onMove);
    };
  }, [ref]);
}

/**
 * scrollsTowards is whether a finger moving by (dx, dy) over target scrolls
 * something between it and root. Moving the finger up (dy < 0) scrolls a box
 * down, so it needs room below; moving it down needs room above. Sideways
 * moves only ask whether a box scrolls sideways at all: which end is which
 * flips with the text's direction, and a sideways swipe does not scroll the
 * book anyway.
 */
export function scrollsTowards(
  target: EventTarget | null,
  root: HTMLElement,
  dx: number,
  dy: number,
): boolean {
  const vertical = Math.abs(dy) >= Math.abs(dx);
  for (let el = target instanceof Element ? target : null; el; el = el.parentElement) {
    if (el instanceof HTMLElement) {
      const style = getComputedStyle(el);
      if (vertical && scrolls(style.overflowY, el)) {
        if (dy < 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
        if (dy > 0 && el.scrollTop > 0) return true;
      }
      if (!vertical && scrolls(style.overflowX, el) && el.scrollWidth > el.clientWidth + 1) {
        return true;
      }
    }
    if (el === root) break;
  }
  return false;
}

/** A textarea scrolls its own lines whatever its overflow says. */
function scrolls(overflow: string, el: HTMLElement): boolean {
  return overflow === "auto" || overflow === "scroll" || el instanceof HTMLTextAreaElement;
}
